import { computed, onMounted, ref, type Component } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { fetchSSOProviders } from '@/api/sso';
import GoogleLogo from '@/components/auth/GoogleLogo.vue';
import MicrosoftLogo from '@/components/auth/MicrosoftLogo.vue';
import en_US from '@/locale/strings/en_US';
import type { SSOFlow, SSOProvider } from '@/types/sso';

/** every provider the frontend can show; the backend decides which of them a deployment offers */
export const SSO_PROVIDERS: Record<SSOProvider, { name: string; logo: Component }> = {
  google: { name: 'Google', logo: GoogleLogo },
  microsoft: { name: 'Microsoft', logo: MicrosoftLogo },
};

export function isSSOProvider(value: unknown): value is SSOProvider {
  return typeof value === 'string' && Object.hasOwn(SSO_PROVIDERS, value);
}

/** shared across pages: the list only changes with the deployment's configuration */
const providers = ref<SSOProvider[]>([]);
let loading: Promise<void> | null = null;

/** where the provider round trip started, so a failed callback can return there */
const FLOW_STORAGE_KEY = 'lead.sso.flow';

/**
 * The SSO providers this deployment offers. `showSSO` is false while they load, when the request fails and when SSO
 * is switched off, so the buttons never flash in and a backend without SSO simply shows none.
 */
export function useSSOProviders() {
  if (!loading) {
    loading = fetchSSOProviders()
      .then((list) => {
        providers.value = list;
      })
      .catch(() => {
        // a missing or failing endpoint means no SSO, and allows a later page to try again
        providers.value = [];
        loading = null;
      });
  }

  return {
    providers,
    showSSO: computed(() => providers.value.length > 0),
  };
}

export function rememberSSOFlow(flow: SSOFlow) {
  try {
    sessionStorage.setItem(FLOW_STORAGE_KEY, flow);
  } catch {
    // storage can be unavailable (private mode); the callback falls back to the login page
  }
}

export function recallSSOFlow(): SSOFlow {
  try {
    return sessionStorage.getItem(FLOW_STORAGE_KEY) === 'registration' ? 'registration' : 'login';
  } catch {
    return 'login';
  }
}

/**
 * The translation key for a backend SSO error code, with a generic fallback. A code is known once it has an
 * `sso.error.<code>` string; English is the fallback of every language, so it holds them all.
 */
export function ssoErrorKey(code: unknown): string {
  if (typeof code !== 'string') return 'sso.error.generic';

  const key = `sso.error.${code}`;
  if (Object.hasOwn(en_US, key)) return key;

  // a new backend code still reads sensibly; the warning says which string to add
  console.warn(`unknown SSO error code: ${code}`);
  return 'sso.error.generic';
}

/**
 * A failure the provider round trip came back with (`?sso_error=`), for the SSO buttons to show once. It is dropped from
 * the url on mount, so a reload does not repeat it.
 */
export function useSSOErrorFromQuery(): string {
  const route = useRoute();
  const router = useRouter();
  const code = typeof route.query.sso_error === 'string' ? route.query.sso_error : '';

  onMounted(() => {
    if (!code) return;
    const { sso_error: _shown, ...query } = route.query;
    router.replace({ query });
  });

  return code;
}
